import { Suspense, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { saveAs } from 'file-saver';
import { useIncrementalNumber } from 'hooks/incremental-number';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PDFPreview from 'views/AdminApp/AdminCandidate/components/FilePreview/PDFPreview';

import { IconButton } from 'components/IconButton/IconButton';

import styles from './IdVerificationSummaryCard.module.scss';

const clamp01 = n => Math.max(0, Math.min(100, Math.round(Number(n ?? 0) * 100) / 100));

export const IdVerificationSummaryCard = ({ idvSummary, loading = false }) => {
  const { t } = useTranslation();

  const { idvStatus, idvStatusKey, faceSimilarity, selfie, idDoc, idDocInfo } = idvSummary || {};

  const faceValue = clamp01(faceSimilarity);
  const [animatedPercent, reset] = useIncrementalNumber(Math.round(faceValue));
  useEffect(() => {
    reset();
  }, []);

  const matchingStatus = useMemo(() => {
    if (!Number.isFinite(faceValue)) return 'bad';
    if (faceValue < 45) return 'bad';
    if (faceValue < 75) return 'average';
    return 'success';
  }, [faceValue]);

  if (loading) {
    return (
      <div className={cn(styles.root, 'background-secondary', 'u-padding')}>
        <div className="card card-with-border u-padding">
          <div className="u-text-centered u-padding">Loading Identity Summary…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.root, 'background-secondary', 'u-padding')}>
      <div className="card card-with-border u-padding">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between">
          <h1 className="title-4 u-bold d-inline-flex align-items-center">
            <FeatherIcon size={18} className="u-margin-right--small" icon="user-check" />
            Face match
          </h1>
          <div className={cn(styles.badge, styles[`badge--${matchingStatus}`])}>
            <FeatherIcon size={16} icon="check" />
            {Number.isFinite(faceValue) ? `${faceValue.toFixed(2)}%` : '—'}
          </div>
        </div>

        <Bar
          percentage={Number.isFinite(faceValue) ? animatedPercent : 0}
          status={matchingStatus}
        />

        {/* Images */}
        <div
          className={cn(
            'd-flex',
            'justify-content-between',
            'align-items-start',
            'gap-small',
            'u-margin-top--large',
          )}>
          <ImageCard upload={selfie} title="Selfie Image" icon="user" />
          <ImageCard upload={idDoc} title="ID Document" icon="file-text" />
        </div>

        {/* Holder name */}
        {idDocInfo?.idHolderName ? (
          <div className="u-margin-top">
            <h1 className="title-4 u-bold">ID Holder</h1>
            <div className={styles.detailsValues}>
              <div className={styles.detailValue}>
                <span className="t-body">{idDocInfo.idHolderName}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Identity document */}
        <div className="u-margin-top">
          <h1 className="title-4 u-bold">
            Identity Document
            {idDocInfo?.status && (
              <Badge className={`u-status-${idDocInfo.status}`} style={{ marginLeft: 8 }}>
                {t(idDocInfo.idDocStatusKey)}
              </Badge>
            )}
          </h1>
          <div className={styles.detailsValues}>
            <div className={styles.detailValue}>
              <span className="title-6">Document Type</span>
              <span className="t-body">{idDocInfo?.idType ?? '—'}</span>
            </div>
            <div className={styles.detailValue}>
              <span className="title-6">Country</span>
              <span className="t-body">{idDocInfo?.idCountryName ?? '—'}</span>
            </div>
            <div className={styles.detailValue}>
              <span className="title-6">ID Number</span>
              <span className="t-body">{idDocInfo?.idNumber ?? '—'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Presentational progress bar */
const Bar = ({ percentage = 0, status = 'bad' }) => (
  <div className={cn(styles.bar, 'u-margin-top', styles[`bar--${status}`])}>
    <div style={{ width: `${clamp01(percentage)}%` }} className={styles.barProgress} />
    <div className={styles.barPercentageTags}>
      <span className={styles.barPercentageTag}>0%</span>
      <span className={styles.barPercentageTag}>50%</span>
      <span className={styles.barPercentageTag}>100%</span>
    </div>
  </div>
);

const ImageCard = ({ upload, title, icon }) => {
  if (!upload?.url) return null;
  const download = () => saveAs(upload.url, upload.file_name || 'document');

  const isSelfie = !!upload?.metadata?.selfie;

  const preferredImgUrl =
    upload?.preview_url ?? (upload?.file_type?.startsWith('image/') ? upload?.url : null);

  const isPdf = !preferredImgUrl && upload?.file_type === 'application/pdf';
  const [pdfLoading, setPdfLoading] = useState(false);

  return (
    <div className={styles.imageCard}>
      <div className={cn('d-flex', 'justify-content-between', 'u-padding--small')}>
        <div className={cn('d-flex', 'align-items-center')}>
          <FeatherIcon icon={icon} size={18} />
          <span className="title-5 u-semi-bold u-padding-left--small">{title}</span>
        </div>
        <IconButton icon="download" type="white" small onClick={download} />
      </div>
      <div className={styles.photoFrame}>
        {isPdf ? (
          <Suspense
            fallback={
              <div className="u-padding u-text-centered">
                <FeatherIcon icon="file" />
              </div>
            }>
            <div className={styles.pdfPreview}>
              <PDFPreview
                file={upload}
                pageNumber={1}
                setIsLoading={setPdfLoading}
                onLoadSuccess={() => setPdfLoading(false)}
                onClick={e => {
                  e?.preventDefault?.();
                  window.open(upload.url, '_blank');
                }}
              />
            </div>
          </Suspense>
        ) : (
          <img
            src={preferredImgUrl}
            alt={title}
            className={cn(
              styles.photo,
              { [styles['photo--cover']]: isSelfie },
              { [styles['photo--contain']]: !isSelfie },
            )}
            loading="lazy"
          />
        )}
      </div>
    </div>
  );
};
